/// <reference types="vite/client" />
import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
} from "@tanstack/react-router";

import { TanStackDevtools } from "@tanstack/react-devtools";
import { ReactQueryDevtoolsPanel } from "@tanstack/react-query-devtools";
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools";

import faviconUrl from "~/assets/icon.svg";
import { authQueryOptions, type AuthQueryResult } from "~/lib/auth/queries";
import appCss from "~/styles.css?url";

import { ThemeProvider } from "~/components/theme-provider";
import { Toaster } from "~/components/ui/sonner";

// Geist fonts (copied to src/assets so we don't rely on geist package exports)
import geistSansUrl from "~/assets/fonts/Geist-Variable.woff2?url";
import geistMonoUrl from "~/assets/fonts/GeistMono-Variable.woff2?url";
import geistPixelSquareUrl from "~/assets/fonts/GeistPixel-Square.woff2?url";
import geistPixelGridUrl from "~/assets/fonts/GeistPixel-Grid.woff2?url";
import geistPixelCircleUrl from "~/assets/fonts/GeistPixel-Circle.woff2?url";
import geistPixelTriangleUrl from "~/assets/fonts/GeistPixel-Triangle.woff2?url";
import geistPixelLineUrl from "~/assets/fonts/GeistPixel-Line.woff2?url";

const geistFontFaces = `
@font-face {
  font-family: "Geist Sans";
  src: url(${geistSansUrl}) format("woff2");
  font-weight: 100 900;
  font-style: normal;
  font-display: swap;
}
@font-face {
  font-family: "Geist Mono";
  src: url(${geistMonoUrl}) format("woff2");
  font-weight: 100 900;
  font-style: normal;
  font-display: swap;
}
@font-face {
  font-family: "Geist Pixel Square";
  src: url(${geistPixelSquareUrl}) format("woff2");
  font-weight: normal;
  font-style: normal;
  font-display: swap;
}
@font-face {
  font-family: "Geist Pixel Grid";
  src: url(${geistPixelGridUrl}) format("woff2");
  font-weight: normal;
  font-style: normal;
  font-display: swap;
}
@font-face {
  font-family: "Geist Pixel Circle";
  src: url(${geistPixelCircleUrl}) format("woff2");
  font-weight: normal;
  font-style: normal;
  font-display: swap;
}
@font-face {
  font-family: "Geist Pixel Triangle";
  src: url(${geistPixelTriangleUrl}) format("woff2");
  font-weight: normal;
  font-style: normal;
  font-display: swap;
}
@font-face {
  font-family: "Geist Pixel Line";
  src: url(${geistPixelLineUrl}) format("woff2");
  font-weight: normal;
  font-style: normal;
  font-display: swap;
}
`;

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
  user: AuthQueryResult;
}>()({
  beforeLoad: ({ context }) => {
    // we're using react-query for client-side caching to reduce client-to-server calls, see /src/router.tsx
    // better-auth's cookieCache is also enabled server-side to reduce server-to-db calls, see /src/lib/auth/auth.ts
    context.queryClient.prefetchQuery(authQueryOptions());

    // typically we don't need the user immediately in landing pages,
    // so we're only prefetching here and not awaiting.
    // for protected routes with loader data, see /_auth/route.tsx
  },
  head: () => ({
    meta: [
      {
        charSet: "utf-8",
      },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1",
      },
      {
        title: "Canopy",
      },
      {
        name: "description",
        content: "Systematic review screening and management platform.",
      },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/svg+xml", href: faviconUrl },
    ],
  }),
  component: RootComponent,
});

function RootComponent() {
  return (
    <RootDocument>
      <Outlet />
    </RootDocument>
  );
}

function RootDocument({ children }: { readonly children: React.ReactNode }) {
  return (
    // suppress since we're updating the theme class in ThemeProvider
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
        <style dangerouslySetInnerHTML={{ __html: geistFontFaces }} />
      </head>
      <body>
        <ThemeProvider forcedTheme="light">
          {children}
          <Toaster richColors />
        </ThemeProvider>

        <TanStackDevtools
          plugins={[
            {
              name: "TanStack Query",
              render: <ReactQueryDevtoolsPanel />,
            },
            {
              name: "TanStack Router",
              render: <TanStackRouterDevtoolsPanel />,
            },
          ]}
        />

        <Scripts />
      </body>
    </html>
  );
}
